import type { ReactNode } from "react";
import { FaFileExcel } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import type { U2000GridStateHandlers } from "../../../reducer/formReducer/U2000GridStateHandlers";
import { u2000FormatIntegerCount } from "../../../tools/U2000NumberFormat";

interface U2000CommonTableActionsProps {
  /** Grille polymorphe : T dépend de l’unité logique ; évite de figer un type ligne unique. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- aligné sur U2000GridStateHandlers<T> des listes LU
  gridStateHandlers: U2000GridStateHandlers<any>;
  onPageChange?: (page: number) => void;
  actionsButtons: ReactNode;
  onPaginationChanged?: (page: number) => void;
  onExportExcel?: () => void | Promise<void>;
  exportExcelTitle?: string;
  isExporting?: boolean;
  hasExportRole?: boolean;
  /** Boutons ou nœuds supplémentaires dans la barre « Actions de recherche et filtrage » (après Export Excel). */
  extraSearchActions?: ReactNode;
}

function U2000CommonTableActions({
  gridStateHandlers,
  actionsButtons,
  onPaginationChanged,
  onExportExcel,
  exportExcelTitle,
  isExporting = false,
  hasExportRole = false,
  extraSearchActions,
}: U2000CommonTableActionsProps) {
  const { i18n } = useTranslation();
  const { page: currentPage, size } = gridStateHandlers.getPagination();
  const totalRecord = gridStateHandlers.getTotalRows();

  // Generate pagination buttons
  const totalPages = Math.ceil(totalRecord / size);
  const maxPagesToShow = 5;
  let startPage, endPage;

  if (totalPages <= maxPagesToShow) {
    startPage = 1;
    endPage = totalPages;
  } else {
    const maxPagesBeforeCurrentPage = Math.ceil(maxPagesToShow / 2) - 1;
    const maxPagesAfterCurrentPage = Math.floor(maxPagesToShow / 2);
    if (currentPage <= maxPagesBeforeCurrentPage) {
      startPage = 1;
      endPage = maxPagesToShow;
    } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
      startPage = totalPages - maxPagesToShow + 1;
      endPage = totalPages;
    } else {
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  const pages = Array.from(Array(endPage + 1 - startPage).keys()).map((i) => startPage + i);

  const handleClick = (action: "First" | "Prev" | "Next" | "Last" | "Page", currentPage: number, page: number) => {
    switch (action) {
      case "First":
        if (currentPage !== 1) {
          onPaginationChanged?.(1);
        }
        break;
      case "Prev":
        if (currentPage !== 1) {
          onPaginationChanged?.(page - 1);
        }
        break;
      case "Next":
        if (currentPage !== totalPages) {
          onPaginationChanged?.(page + 1);
        }
        break;
      case "Last":
        if (currentPage !== totalPages) {
          onPaginationChanged?.(totalPages);
        }
        break;
      default:
        if (currentPage !== page) {
          onPaginationChanged?.(page);
        }
    }
  };

  const showExcel = Boolean(onExportExcel && hasExportRole);
  const showSearchActionsToolbar = showExcel || Boolean(extraSearchActions);

  return (
    <div className="flex flex-col space-y-4 lg:space-y-0 lg:flex-row lg:items-center lg:justify-between">
      {/* Action Buttons */}
      <section className="flex-1" aria-label="Actions de la table" role="toolbar">
        <div className="flex flex-wrap items-center gap-3">{actionsButtons}</div>
      </section>

      {/* Controls: Pagination + Search Actions — min-w-0 évite que le bloc pousse hors carte ; défilement horizontal si besoin */}
      <div className="flex min-w-0 w-full max-w-full flex-col space-y-3 lg:w-auto lg:max-w-none lg:flex-row lg:items-center lg:space-x-4 lg:space-y-0">
        {/* Pagination Controls */}
        <nav className="flex shrink-0 items-center gap-0.5 bg-gray-50 rounded-xl border border-gray-200 p-1" aria-label="Navigation de pagination" role="navigation">
          {/* First Page */}
          <button
            onClick={() => handleClick("First", currentPage, 1)}
            disabled={currentPage === 1}
            className="flex items-center justify-center w-9 h-9 text-sm font-medium rounded-lg transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent
              enabled:hover:bg-white enabled:hover:text-blue-600 enabled:hover:shadow-xs
              focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            aria-label="Première page"
            title="Première page"
          >
            <span>&laquo;</span>
          </button>

          {/* Previous Page */}
          <button
            onClick={() => handleClick("Prev", currentPage, currentPage)}
            disabled={currentPage === 1}
            className="flex items-center justify-center w-9 h-9 text-sm font-medium rounded-lg transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent
              enabled:hover:bg-white enabled:hover:text-blue-600 enabled:hover:shadow-xs
              focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            aria-label="Page précédente"
            title="Page précédente"
          >
            <span>&lsaquo;</span>
          </button>

          {/* Page Numbers */}
          {pages.map((page) => {
            const formattedPage = u2000FormatIntegerCount(page, i18n.language);
            return (
              <button
                key={page}
                onClick={() => handleClick("Page", currentPage, page)}
                className={`flex items-center justify-center min-w-9 h-9 px-1.5 text-sm font-medium rounded-lg transition-colors duration-200
                focus:outline-hidden focus:ring-2 focus:ring-blue-500 ${page === currentPage ? "bg-blue-600 text-white shadow-xs" : "hover:bg-white hover:text-blue-600 hover:shadow-xs"}`}
                aria-label={`Page ${formattedPage}`}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {formattedPage}
              </button>
            );
          })}

          {/* Next Page */}
          <button
            onClick={() => handleClick("Next", currentPage, currentPage)}
            disabled={currentPage === totalPages}
            className="flex items-center justify-center w-9 h-9 text-sm font-medium rounded-lg transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent
              enabled:hover:bg-white enabled:hover:text-blue-600 enabled:hover:shadow-xs
              focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            aria-label="Page suivante"
            title="Page suivante"
          >
            <span>&rsaquo;</span>
          </button>

          {/* Last Page */}
          <button
            onClick={() => handleClick("Last", currentPage, totalPages)}
            disabled={currentPage === totalPages}
            className="flex items-center justify-center w-9 h-9 text-sm font-medium rounded-lg transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent
              enabled:hover:bg-white enabled:hover:text-blue-600 enabled:hover:shadow-xs
              focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            aria-label="Dernière page"
            title="Dernière page"
          >
            <span>&raquo;</span>
          </button>
        </nav>

        {/* Search Actions (Excel / extras) — icônes critères/tri/colonnes déplacées vers U2000BasicSearch (BL-0407) */}
        {showSearchActionsToolbar ? (
          <div
            className="flex min-h-9 min-w-0 max-w-full flex-nowrap items-center overflow-x-auto overscroll-x-contain bg-gray-50 rounded-xl border border-gray-200 p-1 [-ms-overflow-style:none] scrollbar-thin [&::-webkit-scrollbar]:h-1"
            role="toolbar"
            aria-label="Actions de recherche et filtrage"
          >
            {showExcel && (
              <button
                className="flex shrink-0 items-center justify-center w-9 h-9 text-gray-600 rounded-lg transition-colors duration-200
                hover:bg-white hover:text-blue-600 hover:shadow-xs
                focus:outline-hidden focus:ring-2 focus:ring-blue-500
                disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => onExportExcel?.()}
                disabled={isExporting}
                aria-label={exportExcelTitle || "Exporter vers Excel"}
                title={exportExcelTitle || "Exporter vers Excel"}
              >
                <FaFileExcel size={14} />
              </button>
            )}
            {extraSearchActions}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default U2000CommonTableActions;
